import { useState, useEffect } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from '@/shared/hooks'
import { toast } from 'react-toastify'
import { Loader2, AlertCircle, ArrowLeft, Bell, Pencil, ChevronDown, ChevronUp, ArrowRight, Paperclip, FileText, Image, File, ExternalLink } from 'lucide-react'
import { MarkdownPreview, Sidebar, ConfirmModal } from '@/shared/components'
import { StatusTimeline, ExchangePanel, DiscussionButton } from '../components'
import { useFeatureByShortId, useFeatureHistory, useUserStories, useProject, useTeam, useFeatures, useUpdateFeatureColumn, useValidateUserStory, useFeatureAttachments, getAttachmentUrl } from '../hooks'
import { useCurrentUser } from '@/features/auth'
import type { UserStory, FeatureAttachment } from '../hooks'
import { cn } from '@/shared/lib/utils'

// Feature status badge (Scoping, Development, etc.)
function FeatureStatusBadge({ column }: { column: string }) {
  const { t } = useTranslation()

  const styles: Record<string, string> = {
    backlog: 'bg-slate-500',
    scoping: 'bg-violet-600',
    design: 'bg-pink-500',
    development: 'bg-blue-500',
    testing: 'bg-orange-500',
    done: 'bg-green-500',
  }

  return (
    <span className={cn('rounded-full px-2.5 py-1 text-xs font-medium text-white', styles[column] || styles.backlog)}>
      {t(`kanban.columns.${column}`)}
    </span>
  )
}

// User Story status badge
function USStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation()

  const styles: Record<string, { bg: string; text: string }> = {
    pending_validation: { bg: 'bg-orange-50', text: 'text-orange-500' },
    validated: { bg: 'bg-green-50', text: 'text-green-600' },
    to_do: { bg: 'bg-slate-100', text: 'text-slate-600' },
    in_progress: { bg: 'bg-violet-50', text: 'text-violet-600' },
    testing: { bg: 'bg-amber-50', text: 'text-amber-600' },
    done: { bg: 'bg-green-50', text: 'text-green-600' },
  }

  const style = styles[status] || styles.to_do

  return (
    <span className={cn('rounded-md px-2.5 py-1 text-xs font-medium', style.bg, style.text)}>
      {t(`feature.usStatus.${status}`)}
    </span>
  )
}

// Priority badge
function PriorityBadge({ priority }: { priority: string }) {
  const { t } = useTranslation()

  const styles: Record<string, { bg: string; text: string }> = {
    high: { bg: 'bg-red-100', text: 'text-red-600' },
    medium: { bg: 'bg-amber-100', text: 'text-amber-600' },
    low: { bg: 'bg-green-100', text: 'text-green-600' },
  }

  const style = styles[priority] || styles.medium

  return (
    <span className={cn('rounded-md px-2.5 py-1 text-xs font-medium', style.bg, style.text)}>
      {t(`kanban.priority.${priority}`)}
    </span>
  )
}

function UserStoryCard({
  us,
  onClick,
  onValidate,
  isValidating = false
}: {
  us: UserStory
  onClick?: () => void
  onValidate?: () => void
  isValidating?: boolean
}) {
  const { t } = useTranslation()
  const isPending = us.status === 'pending_validation'
  const isTesting = us.status === 'testing'
  const needsAction = isPending || isTesting
  const isDone = us.status === 'done'

  const handleValidate = (e: React.MouseEvent) => {
    e.stopPropagation()
    onValidate?.()
  }

  return (
    <div
      onClick={onClick}
      className={cn(
        'flex flex-col gap-3 rounded-lg border bg-white p-4 transition-colors',
        isPending && 'border-2 border-orange-500',
        isTesting && 'border-2 border-amber-500',
        !needsAction && 'border-border',
        isDone && 'opacity-70',
        onClick && 'cursor-pointer hover:bg-background-secondary'
      )}
    >
      {/* Header: Title + Badge */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">{us.title}</span>
            {/* Action indicator icon */}
            {needsAction && (
              <Bell className="h-3.5 w-3.5 text-accent" />
            )}
          </div>
          {us.as_a && us.i_want && (
            <p className="text-[13px] text-foreground-muted">
              {us.i_want}
            </p>
          )}
        </div>
        <USStatusBadge status={us.status} />
      </div>

      {/* Action button for pending validation */}
      {isPending && onValidate && (
        <div>
          <button
            onClick={handleValidate}
            disabled={isValidating}
            className="flex items-center gap-2 rounded-md bg-accent px-3.5 py-2 text-[13px] font-medium text-white transition-colors hover:bg-accent/90 disabled:opacity-50"
          >
            {isValidating && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t('feature.reviewValidate')}
          </button>
        </div>
      )}
    </div>
  )
}

// Collapsible section component
function CollapsibleSection({
  title,
  children,
  defaultOpen = true
}: {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-white">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between border-b border-border px-5 py-4 text-left hover:bg-background-secondary/50"
      >
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {isOpen ? (
          <ChevronUp className="h-4 w-4 text-foreground-muted" />
        ) : (
          <ChevronDown className="h-4 w-4 text-foreground-muted" />
        )}
      </button>
      {isOpen && <div className="p-5">{children}</div>}
    </div>
  )
}

// Info field component (plain text)
function InfoField({ label, value, hideIfEmpty = false }: { label: string; value?: string | null; hideIfEmpty?: boolean }) {
  const { t } = useTranslation()

  if (hideIfEmpty && !value) return null

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-foreground-muted">{label}</span>
      <p className="whitespace-pre-wrap text-sm text-foreground">
        {value || t('feature.noContent')}
      </p>
    </div>
  )
}

// Markdown field component (renders markdown with Mermaid support)
function MarkdownField({ label, value, hideIfEmpty = false }: { label: string; value?: string | null; hideIfEmpty?: boolean }) {
  const { t } = useTranslation()

  if (hideIfEmpty && !value) return null

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-foreground-muted">{label}</span>
      {value ? (
        <MarkdownPreview content={value} />
      ) : (
        <p className="text-sm text-foreground">{t('feature.noContent')}</p>
      )}
    </div>
  )
}

function formatDate(dateString: string | null | undefined, includeTime = false): string {
  if (!dateString) return '-'
  const date = new Date(dateString)
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
  if (includeTime) {
    return date.toLocaleDateString('en-US', options) + ' at ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
  }
  return date.toLocaleDateString('en-US', options)
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return Image
  if (mimeType === 'application/pdf') return FileText
  return File
}

function AttachmentCard({ attachment }: { attachment: FeatureAttachment }) {
  const [url, setUrl] = useState<string>('')
  const FileIcon = getFileIcon(attachment.mime_type)
  const isImage = attachment.mime_type.startsWith('image/')
  const uploaderName = attachment.uploader?.full_name || attachment.uploader?.email || 'Unknown'

  useEffect(() => {
    getAttachmentUrl(attachment.storage_path).then(setUrl)
  }, [attachment.storage_path])

  if (!url) {
    return (
      <div className="flex h-20 items-center justify-center rounded-lg border border-border bg-background-secondary">
        <Loader2 className="h-4 w-4 animate-spin text-foreground-muted" />
      </div>
    )
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col overflow-hidden rounded-lg border border-border bg-white transition-colors hover:border-accent"
    >
      {isImage ? (
        <div className="relative h-20 overflow-hidden bg-slate-100">
          <img
            src={url}
            alt={attachment.file_name}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all group-hover:bg-black/20 group-hover:opacity-100">
            <ExternalLink className="h-5 w-5 text-white" />
          </div>
        </div>
      ) : (
        <div className="flex h-20 items-center justify-center bg-slate-50">
          <FileIcon className="h-8 w-8 text-foreground-muted" />
        </div>
      )}
      <div className="flex flex-col gap-0.5 p-2">
        <span className="truncate text-xs font-medium text-foreground" title={attachment.file_name}>
          {attachment.file_name}
        </span>
        <div className="flex items-center gap-1 text-[10px] text-foreground-muted">
          <span>{formatFileSize(attachment.file_size)}</span>
          <span>•</span>
          <span className="truncate">{uploaderName}</span>
        </div>
      </div>
    </a>
  )
}

export function FeatureDetailPage() {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { shortId } = useParams<{ shortId: string }>()

  const { data: feature, isLoading: isLoadingFeature, error: featureError } = useFeatureByShortId(shortId)
  const { data: userStories, isLoading: isLoadingUS } = useUserStories(feature?.id)
  const { data: featureHistory } = useFeatureHistory(feature?.id)
  const { data: project } = useProject(feature?.project_id)
  const { data: team } = useTeam(project?.team_id)
  const { data: attachments } = useFeatureAttachments(feature?.id)
  const { user } = useCurrentUser()
  const updateColumn = useUpdateFeatureColumn()
  const validateMutation = useValidateUserStory()

  const attachmentCount = attachments?.length || 0

  const [validatingUsId, setValidatingUsId] = useState<string | null>(null)
  const [confirmModal, setConfirmModal] = useState<'scoping' | 'design' | 'development' | null>(null)
  const [showOnlyActions, setShowOnlyActions] = useState(false)
  const [isExchangePanelOpen, setIsExchangePanelOpen] = useState(false)

  // Fetch all features to check WIP limit
  const { data: allFeatures } = useFeatures(feature?.project_id)

  const isLoading = isLoadingFeature || isLoadingUS
  const error = featureError

  const handleBackClick = () => {
    if (project?.short_id) {
      navigate(`/p/${project.short_id}`)
    } else {
      navigate('/dashboard')
    }
  }

  const handleValidateUS = (usId: string) => {
    setValidatingUsId(usId)
    validateMutation.mutate(usId, {
      onSuccess: () => {
        toast.success(t('us.validated'))
      },
      onError: (error) => {
        toast.error(error.message)
      },
      onSettled: () => {
        setValidatingUsId(null)
      },
    })
  }

  // Count pending validation US
  const pendingCount = userStories?.filter((us) => us.status === 'pending_validation').length || 0

  // Count US needing validation (not yet validated)
  const usNeedingValidation = userStories?.filter((us) => us.status === 'pending_validation').length || 0

  // Check if all US are validated (status = 'validated' or beyond)
  const validatedStatuses = ['validated', 'to_do', 'in_progress', 'testing', 'done']
  const allUSValidated = userStories && userStories.length > 0 &&
    userStories.every((us) => validatedStatuses.includes(us.status))

  // Conditions for Move to Design button
  const isInScoping = feature?.kanban_column === 'scoping'
  const canEdit = user?.role === 'admin' || user?.role === 'user' // Not viewer
  const canMoveToDesign = isInScoping && allUSValidated && canEdit

  // Conditions for Move to Development button
  const isInDesign = feature?.kanban_column === 'design'
  const canMoveToDevelopment = isInDesign && canEdit // No blocking condition

  // Conditions for Start Scoping button (Backlog → Scoping)
  const isInBacklog = feature?.kanban_column === 'backlog'
  const scopingCount = allFeatures?.filter((f) => f.kanban_column === 'scoping').length || 0
  const wipLimitReached = scopingCount >= 1
  const canStartScoping = isInBacklog && canEdit && !wipLimitReached

  const handleStartScoping = () => {
    if (!feature?.id || !canEdit || !isInBacklog) return
    setConfirmModal('scoping')
  }

  const handleMoveToDesign = () => {
    if (!feature?.id || !canMoveToDesign) return
    setConfirmModal('design')
  }

  const handleMoveToDevelopment = () => {
    if (!feature?.id || !canMoveToDevelopment) return
    setConfirmModal('development')
  }

  const handleConfirmTransition = () => {
    if (!feature?.id) return

    const columnMap = {
      scoping: 'scoping',
      design: 'design',
      development: 'development',
    } as const
    const column = columnMap[confirmModal!]

    const successMessages = {
      scoping: t('feature.scopingStarted'),
      design: t('feature.movedToDesign'),
      development: t('feature.movedToDevelopment'),
    }

    updateColumn.mutate(
      { featureId: feature.id, column },
      {
        onSuccess: () => {
          setConfirmModal(null)
          toast.success(successMessages[confirmModal!])
        },
        onError: (error) => {
          toast.error(error.message)
        },
      }
    )
  }

  // Build breadcrumb
  const breadcrumb = [
    team?.name || 'Team',
    project?.name || 'Project',
    'Features'
  ].join(' / ')

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />

      <main className="flex flex-1 flex-col overflow-hidden bg-background-secondary">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 px-8 py-6">
          <div className="flex items-center gap-4">
            {/* Back button */}
            <button
              onClick={handleBackClick}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-white text-foreground transition-colors hover:bg-background-secondary"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>

            {/* Title group */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-foreground-muted">
                {breadcrumb}
              </span>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-semibold text-foreground">
                  {feature?.name || t('feature.title')}
                </h1>
                {feature?.kanban_column && (
                  <FeatureStatusBadge column={feature.kanban_column} />
                )}
                {feature?.priority && (
                  <PriorityBadge priority={feature.priority} />
                )}
              </div>
            </div>
          </div>

          {/* Right side: action badge + transition button + edit button */}
          <div className="flex items-center gap-3">
            {/* Compact action badge - clickable to scroll to US */}
            {pendingCount > 0 && (
              <button
                onClick={() => {
                  const element = document.getElementById('user-stories-section')
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }
                }}
                title={t('feature.actionsTooltip', { count: pendingCount })}
                className="flex h-10 items-center gap-1.5 rounded-lg border border-accent bg-orange-50 px-3 transition-colors hover:bg-orange-100"
              >
                <Bell className="h-4 w-4 text-accent" />
                <span className="text-sm font-semibold text-accent">{pendingCount}</span>
              </button>
            )}
            {/* Start Scoping button - only shown in Backlog phase for Admin/User */}
            {isInBacklog && canEdit && (
              <button
                onClick={handleStartScoping}
                disabled={!canStartScoping || updateColumn.isPending}
                title={wipLimitReached ? t('feature.cannotStartScopingWip') : undefined}
                className={cn(
                  'flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-medium transition-colors',
                  canStartScoping && !updateColumn.isPending
                    ? 'bg-success text-white hover:bg-success/90'
                    : 'cursor-not-allowed bg-slate-200 text-slate-400'
                )}
              >
                {updateColumn.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
                {updateColumn.isPending ? t('feature.startingScoping') : t('feature.startScoping')}
              </button>
            )}
            {/* Move to Design button - only shown in Scoping phase for Admin/User */}
            {isInScoping && canEdit && (
              <button
                onClick={handleMoveToDesign}
                disabled={!canMoveToDesign || updateColumn.isPending}
                title={!canMoveToDesign ? t('feature.cannotProceedValidation', { count: usNeedingValidation }) : undefined}
                className={cn(
                  'flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-medium transition-colors',
                  canMoveToDesign && !updateColumn.isPending
                    ? 'bg-success text-white hover:bg-success/90'
                    : 'cursor-not-allowed bg-slate-200 text-slate-400'
                )}
              >
                {updateColumn.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
                {updateColumn.isPending ? t('feature.movingToDesign') : t('feature.moveToDesign')}
              </button>
            )}
            {/* Move to Development button - only shown in Design phase for Admin/User */}
            {isInDesign && canEdit && (
              <button
                onClick={handleMoveToDevelopment}
                disabled={updateColumn.isPending}
                className="flex h-10 items-center gap-2 rounded-lg bg-success px-4 text-sm font-medium text-white transition-colors hover:bg-success/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {updateColumn.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
                {updateColumn.isPending ? t('feature.movingToDevelopment') : t('feature.moveToDevelopment')}
              </button>
            )}
            {/* Attachments button - only shown if there are attachments */}
            {attachmentCount > 0 && (
              <button
                onClick={() => {
                  const element = document.getElementById('attachments-section')
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }
                }}
                title={t('feature.attachmentsTooltip', { count: attachmentCount })}
                className="flex h-10 items-center gap-1.5 rounded-lg border border-border bg-white px-3 text-foreground transition-colors hover:bg-background-secondary"
              >
                <Paperclip className="h-4 w-4" />
                <span className="text-sm font-medium">{attachmentCount}</span>
              </button>
            )}
            {/* Discussion button */}
            {feature?.id && (
              <DiscussionButton
                level="feature"
                scopeId={feature.id}
                onClick={() => setIsExchangePanelOpen(true)}
              />
            )}
            {/* Edit button */}
            <button className="flex h-10 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white transition-colors hover:bg-primary/90">
              <Pencil className="h-4 w-4" />
              {t('feature.editFeature')}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 pb-8">
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-foreground-muted" />
            </div>
          ) : error ? (
            <div className="flex h-64 flex-col items-center justify-center gap-2 text-destructive">
              <AlertCircle className="h-8 w-8" />
              <p>{t('common.error')}</p>
            </div>
          ) : (
            <div className="flex gap-6">
              {/* Left column - Feature Details */}
              <div className="flex w-[400px] flex-shrink-0 flex-col gap-4">
                {/* Initial Request Section */}
                <CollapsibleSection title={t('feature.initialRequest')}>
                  <div className="flex flex-col gap-4">
                    {/* Feature name */}
                    <div className="flex flex-col gap-1.5">
                      <span className="text-xs font-medium text-foreground-muted">
                        {t('feature.title')}
                      </span>
                      <p className="text-sm font-medium text-foreground">
                        {feature?.name}
                      </p>
                    </div>

                    {/* Priority */}
                    <div className="flex flex-col gap-1.5">
                      <span className="text-xs font-medium text-foreground-muted">
                        {t('feature.priority')}
                      </span>
                      <div>
                        <PriorityBadge priority={feature?.priority || 'medium'} />
                      </div>
                    </div>

                    <MarkdownField
                      label={t('feature.objective')}
                      value={feature?.objective}
                    />
                    <MarkdownField
                      label={t('feature.expectedOutcome')}
                      value={feature?.expected_outcome}
                      hideIfEmpty
                    />
                    <InfoField
                      label={t('feature.targetUsers')}
                      value={feature?.target_users}
                      hideIfEmpty
                    />
                    <MarkdownField
                      label={t('feature.businessRules')}
                      value={feature?.business_rules}
                      hideIfEmpty
                    />
                    <MarkdownField
                      label={t('feature.acceptanceCriteria')}
                      value={feature?.acceptance_criteria}
                      hideIfEmpty
                    />
                    <MarkdownField
                      label={t('feature.technicalConstraints')}
                      value={feature?.technical_constraints}
                      hideIfEmpty
                    />

                    {/* Created by ebuilder */}
                    <div className="mt-2 flex items-center gap-2 border-t border-border pt-4 text-xs text-foreground-muted">
                      <span>{t('feature.createdBy')}</span>
                      <span className="font-medium text-foreground">HeyNicolai</span>
                      <span>•</span>
                      <span>{formatDate(feature?.created_at)}</span>
                    </div>
                  </div>
                </CollapsibleSection>

                {/* Specifications Section */}
                <CollapsibleSection title={t('feature.specifications')}>
                  {feature?.spec ? (
                    <div className="flex flex-col gap-4">
                      {/* Spec content with Mermaid support */}
                      <MarkdownPreview content={feature.spec} />
                      {/* Written by ebuilder + last updated */}
                      <div className="flex items-center gap-2 border-t border-border pt-4 text-xs text-foreground-muted">
                        <span>{t('feature.writtenBy')}</span>
                        <span className="font-medium text-foreground">HeyNicolai</span>
                        <span>•</span>
                        <span>{t('feature.specLastUpdated')}</span>
                        <span>{formatDate(feature.updated_at)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <p className="text-sm italic text-foreground-muted">
                        {t('feature.noSpec')}
                      </p>
                      <p className="text-xs text-foreground-subtle">
                        {t('feature.noSpecNotice')}
                      </p>
                    </div>
                  )}
                </CollapsibleSection>

                {/* Attachments Section - only shown if there are attachments */}
                {attachmentCount > 0 && (
                  <div id="attachments-section">
                    <CollapsibleSection title={t('feature.attachments')}>
                      <div className="grid grid-cols-3 gap-3">
                        {attachments?.map((attachment) => (
                          <AttachmentCard key={attachment.id} attachment={attachment} />
                        ))}
                      </div>
                      <p className="mt-3 text-[10px] text-foreground-subtle">
                        {t('feature.attachmentsHint')}
                      </p>
                    </CollapsibleSection>
                  </div>
                )}

                {/* Feature Info Section */}
                <CollapsibleSection title={t('feature.details')} defaultOpen={false}>
                  <div className="flex flex-col gap-4">
                    {/* Priority */}
                    <div className="flex flex-col gap-1.5">
                      <span className="text-xs font-medium text-foreground-muted">
                        {t('feature.priority')}
                      </span>
                      <div>
                        <PriorityBadge priority={feature?.priority || 'medium'} />
                      </div>
                    </div>

                    {/* Created */}
                    <InfoField
                      label={t('feature.created')}
                      value={formatDate(feature?.created_at)}
                    />

                    {/* Last Updated */}
                    <InfoField
                      label={t('feature.lastUpdated')}
                      value={formatDate(feature?.updated_at, true)}
                    />
                  </div>
                </CollapsibleSection>

                {/* History Section */}
                <CollapsibleSection title={t('feature.history')} defaultOpen={false}>
                  {featureHistory && featureHistory.length > 0 ? (
                    <StatusTimeline
                      entries={featureHistory}
                      createdAt={feature?.created_at || ''}
                    />
                  ) : (
                    <p className="text-sm text-foreground-muted">{t('feature.noHistory')}</p>
                  )}
                </CollapsibleSection>
              </div>

              {/* Right column - User Stories */}
              <div id="user-stories-section" className="flex flex-1 flex-col gap-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="text-base font-semibold text-foreground">
                      {t('feature.userStories')}
                    </h3>
                    {userStories && userStories.length > 0 && (
                      <>
                        {/* Contextual counter based on feature phase */}
                        {feature?.kanban_column === 'scoping' ? (
                          // During Scoping: show validated count
                          <span className={cn(
                            'rounded-full border px-2.5 py-0.5 text-xs font-medium',
                            userStories.filter((us) => validatedStatuses.includes(us.status)).length === userStories.length
                              ? 'border-green-200 bg-green-50 text-green-600'
                              : 'border-border bg-white text-foreground-muted'
                          )}>
                            {t('feature.usValidated', {
                              validated: userStories.filter((us) => validatedStatuses.includes(us.status)).length,
                              total: userStories.length
                            })}
                          </span>
                        ) : feature?.kanban_column === 'testing' ? (
                          // During Testing: show completed count
                          <span className={cn(
                            'rounded-full border px-2.5 py-0.5 text-xs font-medium',
                            userStories.filter((us) => us.status === 'done').length === userStories.length
                              ? 'border-green-200 bg-green-50 text-green-600'
                              : 'border-border bg-white text-foreground-muted'
                          )}>
                            {t('feature.usCompleted', {
                              completed: userStories.filter((us) => us.status === 'done').length,
                              total: userStories.length
                            })}
                          </span>
                        ) : (
                          // Default: show completed count
                          <span className="rounded-full border border-border bg-white px-2.5 py-0.5 text-xs font-medium text-foreground-muted">
                            {t('feature.usCompleted', {
                              completed: userStories.filter((us) => us.status === 'done').length,
                              total: userStories.length
                            })}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {/* Filter: Show only my actions */}
                  {userStories && userStories.some((us) => us.status === 'pending_validation' || us.status === 'testing') && (
                    <button
                      onClick={() => setShowOnlyActions(!showOnlyActions)}
                      className={cn(
                        'flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                        showOnlyActions
                          ? 'border-accent bg-accent/10 text-accent'
                          : 'border-border bg-white text-foreground-muted hover:bg-background-secondary'
                      )}
                    >
                      <Bell className="h-3.5 w-3.5" />
                      {t('feature.showOnlyActions')}
                    </button>
                  )}
                </div>

                {/* US List */}
                {userStories && userStories.length > 0 ? (
                  <div className="flex flex-col gap-3">
                    {userStories
                      .filter((us) => !showOnlyActions || us.status === 'pending_validation' || us.status === 'testing')
                      .map((us) => (
                        <UserStoryCard
                          key={us.id}
                          us={us}
                          onClick={() => navigate(`/us/${us.short_id}`)}
                          onValidate={() => handleValidateUS(us.id)}
                          isValidating={validatingUsId === us.id}
                        />
                      ))}
                    {/* Empty state when filter is active but no matching US */}
                    {showOnlyActions && !userStories.some((us) => us.status === 'pending_validation' || us.status === 'testing') && (
                      <div className="rounded-lg border border-dashed border-border bg-white p-8 text-center">
                        <p className="text-sm text-foreground-muted">
                          {t('feature.noActionsRequired')}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-border bg-white p-8 text-center">
                    <p className="text-sm text-foreground-muted">
                      {t('feature.noUserStories')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Exchange Panel (overlay) */}
      {feature?.id && (
        <ExchangePanel
          isOpen={isExchangePanelOpen}
          onClose={() => setIsExchangePanelOpen(false)}
          level="feature"
          scopeId={feature.id}
          title={t('exchange.featureTitle')}
          subtitle={feature.name}
        />
      )}

      {/* Confirmation Modal for transitions */}
      <ConfirmModal
        isOpen={confirmModal !== null}
        onClose={() => setConfirmModal(null)}
        onConfirm={handleConfirmTransition}
        isLoading={updateColumn.isPending}
        title={
          confirmModal === 'scoping'
            ? t('feature.startScopingConfirm')
            : confirmModal === 'design'
              ? t('feature.moveToDesignConfirm')
              : t('feature.moveToDevelopmentConfirm')
        }
        description={
          confirmModal === 'scoping'
            ? t('feature.startScopingConfirmDesc')
            : confirmModal === 'design'
              ? t('feature.moveToDesignConfirmDesc')
              : t('feature.moveToDevelopmentConfirmDesc')
        }
        confirmLabel={
          confirmModal === 'scoping'
            ? t('feature.startScoping')
            : confirmModal === 'design'
              ? t('feature.moveToDesign')
              : t('feature.moveToDevelopment')
        }
      />
    </div>
  )
}
